import { type AuthBootstrapResponse } from '@contract-analyzer/api-client';
import {
  PublicClientApplication,
  type AuthenticationResult,
} from '@azure/msal-browser';

import { apiClient } from '@/lib/api';

type AuthMode = 'backend-oidc' | 'entra-msal';

type InteractiveSignInConfig = {
  readonly clientId: string;
  readonly redirectUri: string;
  readonly scopes: readonly string[];
  readonly tenantId: string;
};

export type IdentityTokenAcquisitionErrorKind =
  | 'interaction_required'
  | 'cancelled'
  | 'in_progress'
  | 'misconfigured'
  | 'failed';

export type IdentityTokenResult = {
  readonly token: string;
};

export interface IdentityProviderClient {
  signInInteractive(): Promise<IdentityTokenResult>;
  acquireIdentityTokenSilent(): Promise<IdentityTokenResult>;
  signOut?(): Promise<void>;
}

export class IdentityTokenAcquisitionError extends Error {
  readonly kind: IdentityTokenAcquisitionErrorKind;

  constructor(
    kind: IdentityTokenAcquisitionErrorKind,
    message: string,
    options: { readonly cause?: unknown } = {},
  ) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause });
    this.name = 'IdentityTokenAcquisitionError';
    this.kind = kind;
  }
}

export type InteractiveSessionBootstrapResult = {
  readonly bootstrap: AuthBootstrapResponse;
  readonly token: string;
};

export type DevPersona = {
  readonly email: string;
  readonly name: string;
  readonly sub?: string;
  readonly tid?: string;
};

const DEFAULT_SIGN_IN_SCOPES = ['openid', 'profile', 'email'];
const SILENT_INTERACTION_REQUIRED_CODES = new Set([
  'consent_required',
  'interaction_required',
  'login_required',
  'no_account_error',
]);

let msalClientPromise: Promise<PublicClientApplication> | null = null;

function readTrimmedString(value: string | null | undefined): string | null {
  const trimmedValue = value?.trim();
  return trimmedValue ? trimmedValue : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function resolveAuthMode(): AuthMode {
  return import.meta.env['VITE_AUTH_MODE'] === 'entra-msal' ? 'entra-msal' : 'backend-oidc';
}

function resolveDefaultRedirectUri(): string {
  if (
    typeof window === 'undefined' ||
    !window.location.origin ||
    window.location.origin === 'null'
  ) {
    return 'https://localhost/';
  }

  const basePath = import.meta.env.BASE_URL.replace(/\/+$/, '') || '';
  return new URL(basePath || '/', window.location.origin).toString();
}

function resolveScopes(rawScopes: string | null): readonly string[] {
  if (rawScopes === null) {
    return DEFAULT_SIGN_IN_SCOPES;
  }

  const scopes = rawScopes
    .split(/[\s,]+/)
    .map((scope) => scope.trim())
    .filter((scope) => scope.length > 0);
  return scopes.length > 0 ? scopes : DEFAULT_SIGN_IN_SCOPES;
}

function resolveInteractiveSignInConfig(): InteractiveSignInConfig {
  const clientId = readTrimmedString(import.meta.env['VITE_ENTRA_CLIENT_ID']);
  const tenantId = readTrimmedString(import.meta.env['VITE_ENTRA_TENANT_ID']);
  if (clientId === null || tenantId === null) {
    throw new IdentityTokenAcquisitionError(
      'misconfigured',
      'Interactive Entra sign-in is not configured for this environment.',
    );
  }

  return {
    clientId,
    redirectUri:
      readTrimmedString(import.meta.env['VITE_ENTRA_REDIRECT_URI']) ?? resolveDefaultRedirectUri(),
    scopes: resolveScopes(readTrimmedString(import.meta.env['VITE_ENTRA_SCOPES'])),
    tenantId,
  };
}

function getErrorCode(error: unknown): string | null {
  if (!isRecord(error) || !('errorCode' in error)) {
    return null;
  }

  const errorCode = error['errorCode'];
  return typeof errorCode === 'string' && errorCode.length > 0 ? errorCode : null;
}

function toIdentityTokenAcquisitionError(error: unknown): IdentityTokenAcquisitionError {
  if (error instanceof IdentityTokenAcquisitionError) {
    return error;
  }

  const errorCode = getErrorCode(error);
  if (errorCode !== null && SILENT_INTERACTION_REQUIRED_CODES.has(errorCode)) {
    return new IdentityTokenAcquisitionError(
      'interaction_required',
      'Your secure session needs confirmation before it can continue.',
      { cause: error },
    );
  }

  switch (errorCode) {
    case 'interaction_in_progress':
      return new IdentityTokenAcquisitionError(
        'in_progress',
        'Another sign-in is already in progress. Wait for it to complete or refresh the page.',
        { cause: error },
      );
    case 'monitor_window_timeout':
      return new IdentityTokenAcquisitionError(
        'failed',
        'Sign-in timed out before a backend session could be created.',
        { cause: error },
      );
    case 'popup_window_error':
      return new IdentityTokenAcquisitionError(
        'failed',
        'Sign-in could not open a popup window. Allow popups and try again.',
        { cause: error },
      );
    case 'user_cancelled':
      return new IdentityTokenAcquisitionError(
        'cancelled',
        'Sign-in was cancelled before a backend session could be created.',
        { cause: error },
      );
    default:
      if (error instanceof Error && error.message.trim().length > 0) {
        return new IdentityTokenAcquisitionError('failed', error.message, { cause: error });
      }
      return new IdentityTokenAcquisitionError(
        'failed',
        'Sign-in failed before a backend session could be created.',
        { cause: error },
      );
  }
}

async function getMsalClient(): Promise<PublicClientApplication> {
  const config = resolveInteractiveSignInConfig();
  if (msalClientPromise === null) {
    const client = new PublicClientApplication({
      auth: {
        authority: `https://login.microsoftonline.com/${config.tenantId}`,
        clientId: config.clientId,
        redirectUri: config.redirectUri,
      },
      cache: {
        cacheLocation: 'sessionStorage',
      },
    });
    msalClientPromise = client
      .initialize()
      .then(() => client.handleRedirectPromise())
      .then(() => client);
  }
  return msalClientPromise;
}

function readIdentityToken(result: AuthenticationResult): IdentityTokenResult {
  const token = readTrimmedString(result.idToken);
  if (token === null) {
    throw new IdentityTokenAcquisitionError(
      'failed',
      'Sign-in completed without an ID token.',
    );
  }
  return { token };
}

function createEntraMsalIdentityProviderClient(): IdentityProviderClient {
  return {
    async signInInteractive() {
      try {
        const config = resolveInteractiveSignInConfig();
        const client = await getMsalClient();
        const result = await client.loginPopup({
          redirectUri: config.redirectUri,
          scopes: [...config.scopes],
        });

        client.setActiveAccount(result.account);
        return readIdentityToken(result);
      } catch (error) {
        throw toIdentityTokenAcquisitionError(error);
      }
    },
    async acquireIdentityTokenSilent() {
      try {
        const config = resolveInteractiveSignInConfig();
        const client = await getMsalClient();
        const account = client.getActiveAccount() ?? client.getAllAccounts()[0] ?? null;
        if (account === null) {
          throw new IdentityTokenAcquisitionError(
            'interaction_required',
            'Your secure session needs confirmation before it can continue.',
          );
        }

        const result = await client.acquireTokenSilent({
          account,
          scopes: [...config.scopes],
        });
        return readIdentityToken(result);
      } catch (error) {
        throw toIdentityTokenAcquisitionError(error);
      }
    },
  };
}

export function usesInteractiveSessionBootstrap(): boolean {
  return resolveAuthMode() === 'entra-msal';
}

export function isDevAuthMode(): boolean {
  return resolveAuthMode() === 'backend-oidc';
}

function encodeDevToken(persona: DevPersona): string {
  const payload = {
    sub: persona.sub ?? `dev-${persona.email}`,
    email: persona.email,
    name: persona.name,
    tid: persona.tid ?? 'dev-local',
  };
  const jsonBytes = new TextEncoder().encode(JSON.stringify(payload));
  let binary = '';
  for (const byte of jsonBytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
}

export function createDevIdentityProviderClient(persona: DevPersona): IdentityProviderClient {
  const tokenResult = { token: encodeDevToken(persona) };
  return {
    signInInteractive: () => Promise.resolve(tokenResult),
    acquireIdentityTokenSilent: () => Promise.resolve(tokenResult),
  };
}

function createBrowserIdentityProviderClient(): IdentityProviderClient {
  if (!usesInteractiveSessionBootstrap()) {
    throw new IdentityTokenAcquisitionError(
      'misconfigured',
      'Interactive browser sign-in is not enabled for this environment.',
    );
  }
  return createEntraMsalIdentityProviderClient();
}

async function bootstrapSessionWithIdentityToken(
  tokenResult: IdentityTokenResult,
): Promise<InteractiveSessionBootstrapResult> {
  return {
    bootstrap: await apiClient.bootstrapIdentityFromToken(tokenResult.token),
    token: tokenResult.token,
  };
}

export async function startDevSessionBootstrap(
  persona: DevPersona,
): Promise<InteractiveSessionBootstrapResult> {
  return await bootstrapSessionWithIdentityToken(
    await createDevIdentityProviderClient(persona).signInInteractive(),
  );
}

/**
 * An identity token from the provider's sign-in prompt, without starting a session: a renewal
 * the user confirms bootstraps from it as a silent renewal does from its own token.
 */
export async function signInInteractively(): Promise<IdentityTokenResult> {
  return await createBrowserIdentityProviderClient().signInInteractive();
}

export async function startInteractiveSessionBootstrap(): Promise<InteractiveSessionBootstrapResult> {
  return await bootstrapSessionWithIdentityToken(await signInInteractively());
}

export async function acquireIdentityTokenSilent(): Promise<IdentityTokenResult | null> {
  if (!usesInteractiveSessionBootstrap()) {
    return null;
  }
  return await createBrowserIdentityProviderClient().acquireIdentityTokenSilent();
}

export async function acquireGraphAccessTokenSilent(
  scopes: readonly string[],
): Promise<string | null> {
  if (!usesInteractiveSessionBootstrap()) {
    return null;
  }
  try {
    const client = await getMsalClient();
    const account = client.getActiveAccount() ?? client.getAllAccounts()[0] ?? null;
    if (account === null) {
      return null;
    }
    const result = await client.acquireTokenSilent({
      account,
      scopes: [...scopes],
    });
    return result.accessToken.length > 0 ? result.accessToken : null;
  } catch {
    return null;
  }
}
